/**
 * The request-lifecycle reducer every membership slice is built from.
 *
 * One factory rather than three identical reducers: they all track the
 * same lifecycle and differ only in what they keep from the answer.
 * @module reducers/factory
 */

import type { RequestState } from '../types';

/** What Volto's api middleware dispatches. */
export type Action = { type: string; result?: unknown; error?: unknown };

/** Every slice starts here: nothing asked, nothing answered. */
export const initial: RequestState = {
  loading: false,
  loaded: false,
  error: null,
};

/**
 * Build a reducer tracking one request.
 *
 * @param actionType The base action type.
 * @param extract What to keep out of a successful result.
 * @param empty The value of that key before anything has loaded.
 */
export function requestReducer<T>(
  actionType: string,
  extract: (result: any) => T,
  empty: T,
) {
  const initialState = { ...initial, data: empty };
  return function reducer(state = initialState, action: Action = { type: '' }) {
    switch (action.type) {
      case `${actionType}_PENDING`:
        // Clear the data on pending: leaving it in place makes a second attempt
        // look like it succeeded. Keep the current data in a retry scenario by
        // not clearing it here would make the old answer visible while the new
        // one loads, which is wrong.
        return { ...initialState, loading: true };
      case `${actionType}_SUCCESS`:
        return {
          ...state,
          loading: false,
          loaded: true,
          error: null,
          data: extract(action.result),
        };
      case `${actionType}_FAIL`:
        // Keep the data on failure: a retry is about to happen or the user
        // has to deal with the error. Clearing successful data on error just
        // makes the page look broken.
        return {
          ...state,
          loading: false,
          loaded: false,
          error: action.error ?? true,
        };
      default:
        return state;
    }
  };
}
